import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import Popup from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, Text, xcss } from '@atlaskit/primitives';
import { type SearchResultMessagePartial } from '@atlassian/search-client';
import { getFrontendExperiment } from '@atlassian/search-experiment';
import { PersonCard, useCopyToClipboard } from '@atlassian/search-ui/person-card';

import { type MessageBodyDeprecatedProps } from '../../../../../../../../common/ui/message-body-deprecated';
import { ProfileAvatar } from '../../../../../../../../common/ui/profile-avatar';
import {
	getExternalMessageDmIntegrationInfo,
	getUserData,
} from '../../../../../../../../common/utils/search-result-utils';

import { i18n } from './messages';

const threadItemCSS = xcss({
	paddingBottom: 'space.050',
	paddingTop: 'space.050',
	display: 'inline-block',
	verticalAlign: 'bottom',
});

const nameContainer = xcss({
	whiteSpace: 'nowrap',
});

type ThreadBodyProps = Omit<ThreadMessageProps, 'parent'>;

export const ThreadBody = ({ partial, components }: ThreadBodyProps): React.JSX.Element => {
	const linkedEntities = partial?.linkedEntities || [];
	const threadMessages = [partial, ...linkedEntities]
		.slice(0, 3)
		.map((linkedMessage) => (
			<ThreadMessage
				key={linkedMessage.id}
				parent={partial}
				partial={linkedMessage}
				components={components}
			/>
		));

	return <>{threadMessages}</>;
};

interface ThreadMessageProps {
	parent: SearchResultMessagePartial;
	partial: SearchResultMessagePartial;
	components: {
		Body: React.ComponentType<MessageBodyDeprecatedProps>;
	};
}

const popupTriggerXcss = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	padding: 'space.0',
	marginRight: 'space.075',
	gap: 'space.025',
	background: 'none',
	':hover': {
		textDecoration: 'underline',
	},
	fontWeight: 'font.weight.medium',
	font: 'font.body',
	verticalAlign: 'bottom',
});

const regularUserXcss = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	padding: 'space.0',
	marginRight: 'space.075',
	gap: 'space.025',
	background: 'none',
	fontWeight: 'font.weight.medium',
	verticalAlign: 'bottom',
});

const { serveExperiment: serveDoubleLineRenderingModifications } = getFrontendExperiment({
	abTestId: 'slack_result_rendering',
});

const ThreadMessage = ({ parent, partial, components }: ThreadMessageProps) => {
	const [isPersonPopupOpen, setIsPersonPopupOpen] = useState(false);
	const { formatMessage } = useIntl();
	const integrationId = parent.integrationId || partial?.integrationId;

	// Check for user with email priority:
	// createdBy?.thirdPartyUser -> initialContributors with email -> allContributors
	let user = partial?.entity?.createdBy?.thirdPartyUser;
	if (!user) {
		const initialContributor = partial?.initialContributors?.[0];
		if (initialContributor?.email) {
			user = initialContributor;
		} else {
			user = partial?.allContributors?.[0];
		}
	}

	const currentMessageOwnerName = user?.name || formatMessage(i18n.externalPersonLabel);
	const picture = user?.picture;

	const userData = getUserData(user);

	const onCopyEmail = useCopyToClipboard(user?.email);

	const messageSender = (
		<>
			<ProfileAvatar name={currentMessageOwnerName} picture={picture} />
			<Box xcss={nameContainer}>{currentMessageOwnerName}</Box>
		</>
	);

	return (
		<Box xcss={threadItemCSS}>
			<Text maxLines={serveDoubleLineRenderingModifications ? 2 : 1}>
				{userData ? (
					// if we don't have enough data to render the person card we shouldn't make the name
					//	a popup trigger
					<Popup
						isOpen={isPersonPopupOpen}
						onClose={() => setIsPersonPopupOpen(false)}
						content={() =>
							userData ? (
								<PersonCard
									user={userData}
									externalMessageIntegration={getExternalMessageDmIntegrationInfo(
										integrationId,
										user,
									)}
									onCopyEmail={onCopyEmail}
								/>
							) : null
						}
						trigger={(triggerProps) => (
							<Pressable
								xcss={popupTriggerXcss}
								onClick={() => setIsPersonPopupOpen(true)}
								{...triggerProps}
							>
								{messageSender}
							</Pressable>
						)}
					/>
				) : (
					<Box xcss={regularUserXcss}>{messageSender}</Box>
				)}
				<components.Body partial={partial} integrationId={integrationId} />
			</Text>
		</Box>
	);
};
